import { MILESTONE_LABEL, RELATION_LABEL, SEVERITY_LABEL, STATE_LABEL, type ActivityItem, type RelatedTicket, type SupportCase } from './api'

/**
 * The full ticket record as a printable page: details, milestones, resolution, related tickets
 * and every entry on the feed, oldest first. Opened in its own window so the app's navigation is
 * not printed with it; the browser's print dialog also saves it as a PDF.
 */

const esc = (s: string | null | undefined) =>
  (s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')
const at = (iso: string | null | undefined) =>
  iso ? new Date(iso).toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—'

const SOURCE: Record<ActivityItem['kind'], string> = {
  message: 'Tapwork',
  slack: 'Slack thread',
  intercom: 'Intercom',
  intercom_note: 'Intercom note',
  github_comment: 'GitHub',
  github_pr: 'Pull request',
  github_issue: 'GitHub',
  resolution: 'Resolution summary',
}

export function ticketRecordHtml(c: SupportCase, items: ActivityItem[], related: RelatedTicket[], name: (id: string) => string | null): string {
  const rows = (pairs: Array<[string, string | null | undefined]>) =>
    pairs.filter(([, v]) => v).map(([k, v]) => `<tr><th>${esc(k)}</th><td>${esc(v)}</td></tr>`).join('')
  const feed = [
    ...items.map((i) => ({
      at: i.at,
      head: `${esc(i.authorName ?? SOURCE[i.kind])} · ${SOURCE[i.kind]}${i.authorKind === 'customer' ? ' (customer)' : ''}`,
      body: esc(i.body) + (i.files.length ? `<div class="files">Files: ${i.files.map((f) => esc(f.name)).join(', ')}</div>` : ''),
    })),
    ...c.events.map((e) => ({
      at: e.at,
      head: `${esc(e.byName)} · ${esc(e.kind.replace(/_/g, ' '))}${e.to ? ` → ${esc(e.to)}` : ''}`,
      body: esc(e.text ?? '') + (e.evidence ? `<div class="files">Test evidence: ${esc(e.evidence)}</div>` : ''),
    })),
  ].sort((a, b) => Date.parse(a.at) - Date.parse(b.at))
  const resolution = [...items].reverse().find((i) => i.kind === 'resolution' && i.resolution)?.resolution
  return `<!doctype html><html><head><meta charset="utf-8"><title>Support ticket ${esc(c.reference)}</title>
<style>
body{font:13px/1.45 system-ui,-apple-system,Segoe UI,sans-serif;color:rgb(15 23 42);margin:32px;max-width:900px}
h1{font-size:20px;margin:0 0 4px}h2{font-size:14px;margin:24px 0 8px;border-bottom:1px solid rgb(226 232 240);padding-bottom:4px}
.meta{color:rgb(90 100 120);margin-bottom:12px}table{border-collapse:collapse;width:100%}th,td{text-align:left;vertical-align:top;padding:4px 8px;border-bottom:1px solid rgb(241 245 249)}
th{width:190px;color:rgb(90 100 120);font-weight:600}.entry{border-bottom:1px solid rgb(241 245 249);padding:6px 0;break-inside:avoid}
.entry .h{font-weight:600}.entry .t{color:rgb(90 100 120);font-weight:400;margin-left:8px}.entry .b{white-space:pre-wrap;margin-top:2px}.files{color:rgb(90 100 120);font-size:12px}
@media print{body{margin:0}}
</style></head><body>
<h1>${esc(c.title)}</h1>
<div class="meta">Support ticket #${esc(c.reference)} · printed ${esc(at(new Date().toISOString()))}</div>
<table>${rows([
    ['Client', c.clientName ?? 'Not matched'],
    ['Reported by', c.requester],
    ['Channel', c.channel],
    ['Priority', c.severity ? SEVERITY_LABEL[c.severity] : 'Untriaged'],
    ['Status', STATE_LABEL[c.state]],
    ['Team', c.ownerTeam ?? 'Helpdesk'],
    ['Assignee', c.assigneeId ? name(c.assigneeId) : null],
    ['First contact', at(c.firstContactAt)],
    ['Jira', c.jiraKey],
    ['GitHub issue', c.githubUrl],
  ])}</table>
<h2>What the customer reported</h2><div style="white-space:pre-wrap">${esc(c.summary || 'No description.')}</div>
<h2>SLA milestones</h2><table>${c.clock.milestones
    .map((m) => `<tr><th>${MILESTONE_LABEL[m.milestone]}</th><td>${m.doneAt ? `Done ${esc(at(m.doneAt))}` : m.deadlineAt ? `Due ${esc(at(m.deadlineAt))}` : 'Waiting on the customer'} · ${esc(m.status.replace('_', ' '))}</td></tr>`)
    .join('')}</table>
${c.resolutionNote || resolution ? `<h2>Resolution</h2><table>${rows([
    ['Resolution note', c.resolutionNote],
    ['Cause (category)', c.rootCause],
    ['What was wrong', resolution?.problem],
    ['What was done', resolution?.fix],
    ['Cause', resolution?.cause],
    ['Prevention', resolution?.prevention],
  ])}</table>` : ''}
${related.length ? `<h2>Related tickets</h2><table>${related.map((r) => `<tr><th>${RELATION_LABEL[r.kind]}</th><td>#${esc(r.reference)} ${esc(r.title)} · ${STATE_LABEL[r.state]}</td></tr>`).join('')}</table>` : ''}
<h2>Activity</h2>${feed.map((f) => `<div class="entry"><div class="h">${f.head}<span class="t">${esc(at(f.at))}</span></div>${f.body ? `<div class="b">${f.body}</div>` : ''}</div>`).join('') || '<p>No activity.</p>'}
</body></html>`
}

export function printTicketRecord(c: SupportCase, items: ActivityItem[], related: RelatedTicket[], name: (id: string) => string | null): void {
  const win = window.open('', '_blank')
  if (!win) return
  win.document.open()
  win.document.write(ticketRecordHtml(c, items, related, name))
  win.document.close()
  win.focus()
  window.setTimeout(() => win.print(), 300)
}
